/**
 * ExportManager.js - All export-related functions for video parts and groups
 * Extracted from PartsCutter.js for better organization
 */

import { formatTime, formatTimeForFilename, cleanFilename } from '../../utils/partsUtils';
import { getUsedLanguages } from '../../utils/languageUtils';
import { backupPartsAndGroups } from '../../utils/backupUtils';

/**
 * Resolves settings from local storage for a specific preset name or ID
 */
const resolvePresetDetails = (formatId, formatNameOrId) => {
  if (!formatNameOrId) return null;
  try {
    const savedPresets = JSON.parse(localStorage.getItem('formatPresets') || '[]');
    const preset = savedPresets.find(p => p.id === formatNameOrId || p.name === formatNameOrId);
    if (preset) {
      return {
        format: preset.format,
        aspectRatio: preset.aspectRatio,
        customization: preset.customization || null,
        manualCropX: preset.manualCropX !== undefined ? preset.manualCropX : 50,
        manualCropY: preset.manualCropY !== undefined ? preset.manualCropY : 50
      };
    }
  } catch (e) {
    console.error('❌ ExportManager: Error resolving preset details:', e);
  }
  return null;
};

const getBrowserDefaultExportPath = () => {
  try {
    const savedSettings = localStorage.getItem('beevids-settings');
    if (savedSettings) {
      const parsed = JSON.parse(savedSettings);
      const path = parsed.preferences?.defaultExportPath || parsed.defaultExportPath || '';
      if (path && !path.includes('C:\\Users\\Inuobal')) return path.trim();
    }
  } catch (e) {
    console.error('❌ ExportManager: Error loading preferences:', e);
  }
  return '';
};

const selectFolderViaAgentOrPrompt = async (title, defaultPath = '', statusCallback = null) => {
  // If a valid default path was configured in Settings, use it automatically!
  if (defaultPath && defaultPath.trim().length > 0) {
    console.log('✅ ExportManager: Using configured default export path:', defaultPath);
    return defaultPath;
  }

  try {
    const response = await fetch(`http://localhost:8001/select-folder?title=${encodeURIComponent(title)}`);
    const data = await response.json();
    if (data && data.path) {
      return data.path;
    } else {
      if (statusCallback) {
        statusCallback({ type: 'error', message: 'Export canceled: No folder selected.' });
      }
      return null;
    }
  } catch (error) {
    console.error('❌ ExportManager: Failed to open folder picker via Local Agent, falling back to prompt:', error);
    const userEnteredPath = prompt(`${title}:\nEnter the path of the folder where you want to save the export:\n(Make sure the folder exists on your computer)`, defaultPath);
    if (userEnteredPath) {
      return userEnteredPath.trim();
    } else {
      if (statusCallback) {
        statusCallback({ type: 'error', message: 'Export canceled: No folder selected.' });
      }
      return null;
    }
  }
};

const selectFileViaAgentOrPrompt = async (title, defaultPath = '', statusCallback = null) => {
  try {
    const response = await fetch(`http://localhost:8001/select-file?title=${encodeURIComponent(title)}`);
    const data = await response.json();
    if (data && data.path) {
      return data.path;
    } else {
      if (statusCallback) {
        statusCallback({ type: 'error', message: 'Export canceled: No video file selected.' });
      }
      return null;
    }
  } catch (error) {
    console.error('❌ ExportManager: Failed to open file picker via Local Agent, falling back to prompt:', error);
    const userEnteredFile = prompt(`${title}:\nEnter the absolute path of the original video file on your computer:\n(e.g., C:\\Users\\Inuobal\\Desktop\\video.mp4)`, defaultPath);
    if (userEnteredFile) {
      return userEnteredFile.trim();
    } else {
      if (statusCallback) {
        statusCallback({ type: 'error', message: 'Export canceled: No video file selected.' });
      }
      return null;
    }
  }
};

/**
 * Handle exporting a single part
 */
export const handleExportPart = async (part, project, transcript, forcedFolderPath = null, overrideInputPath = null, onUpdate = null, statusCallback = null) => {
  const partName = part.name || part.text?.substring(0, 20) || 'part';
  console.log('🎬 ExportManager: Exporting part:', partName);

  // If the part has multiple formats and we are not already processing a specific format, loop through them
  if (part.formats && part.formats.length > 1 && part._currentFormatIndex === undefined) {
    console.log(`🎬 ExportManager: Part "${partName}" has ${part.formats.length} formats, exporting all...`);
    
    // Select export folder once
    let folderPath = forcedFolderPath;
    if (!folderPath) {
      if (window.require) {
        const { ipcRenderer } = window.require('electron');
        folderPath = await ipcRenderer.invoke('select-folder', {
          title: `Select folder to export "${partName}" in multiple formats`,
          defaultPath: cleanFilename(partName)
        });
      } else {
        folderPath = await selectFolderViaAgentOrPrompt(
          `Select folder to export "${partName}" in multiple formats`,
          getBrowserDefaultExportPath(),
          statusCallback
        );
      }
    }
    
    if (!folderPath) return;
    
    const results = [];
    const formatMap = {
      '16:9': { name: 'YouTube', ratio: '16/9' },
      '9:16': { name: 'Shorts', ratio: '9/16' },
      '1:1': { name: 'Instagram', ratio: '1/1' },
      '4:5': { name: 'Instagram_Portrait', ratio: '4/5' }
    };
    
    for (let i = 0; i < part.formats.length; i++) {
      const formatId = part.formats[i];
      const formatName = part.formatNames && part.formatNames[i];
      
      let formatFolderName = formatName || formatMap[formatId]?.name || formatId;
      formatFolderName = cleanFilename(formatFolderName);
      const currentFolderPath = `${folderPath}/${formatFolderName}`;
      
      const currentFormatType = formatId !== 'original' ? formatId : null;
      const currentAspectRatio = formatMap[formatId]?.ratio || (formatId !== 'original' ? formatId : null);
      
      const resolvedPreset = resolvePresetDetails(formatId, formatName);
      
      // Create a copy of the part with this specific format active
      const singleFormatPart = {
        ...part,
        _currentFormatIndex: i, // flag to prevent recursion loop
        format: resolvedPreset ? resolvedPreset.format : currentFormatType,
        aspectRatio: resolvedPreset ? resolvedPreset.aspectRatio : currentAspectRatio,
        customization: resolvedPreset ? resolvedPreset.customization : part.customization,
        manualCropX: resolvedPreset ? resolvedPreset.manualCropX : part.manualCropX,
        manualCropY: resolvedPreset ? resolvedPreset.manualCropY : part.manualCropY
      };
      
      if (statusCallback) {
        statusCallback({
          type: 'loading',
          message: `Exporting format ${i + 1}/${part.formats.length} (${formatFolderName}) for "${partName}"...`,
          progress: Math.round((i / part.formats.length) * 100)
        });
      }
      
      try {
        const res = await handleExportPart(singleFormatPart, project, transcript, currentFolderPath, overrideInputPath, onUpdate, statusCallback);
        results.push(res);
      } catch (err) {
        console.error(`❌ ExportManager: Failed to export format ${formatFolderName}:`, err);
        if (part.formats.length === 1) throw err;
      }
    }
    
    if (statusCallback) {
      statusCallback({
        type: 'success',
        message: `✅ Exported "${partName}" in ${part.formats.length} formats!`,
        folder: folderPath
      });
    }
    
    return { success: true, folder: folderPath };
  }

  try {
    let selectedLanguage = 'original';
    const hasSubtitles = transcript && transcript.length > 0;
    const availableLanguages = hasSubtitles ? Object.keys(project.translations || {}) : [];
    
    if (hasSubtitles && availableLanguages.length > 0) {
      const uiSelectedLanguage = part.language || project.settings?.subtitleLanguage || 'original';
      const shouldUseTranslation = uiSelectedLanguage && 
                                 uiSelectedLanguage !== 'original' && 
                                 availableLanguages.includes(uiSelectedLanguage);
      
      selectedLanguage = shouldUseTranslation ? uiSelectedLanguage : 'original';
      console.log('🌐 ExportManager: Selected language:', selectedLanguage);
    }

    const fileName = cleanFilename(`${partName}_${formatTimeForFilename(part.startTime)}_${formatTimeForFilename(part.endTime)}.mp4`);
    
    // Crop transcript and translations to only the selected part's range and text selection
    let croppedTranscript = [];
    let croppedTranslations = {};
    if (transcript && transcript.length > 0) {
      // Find segments that overlap with part
      const overlapping = transcript.filter(s => s.startTime < part.endTime && s.endTime > part.startTime);
      if (overlapping.length > 0) {
        // Sort by startTime
        const sorted = [...overlapping].sort((a, b) => a.startTime - b.startTime);
        
        // Prepare cropped segments
        croppedTranscript = sorted.map((seg, idx) => {
          const newSeg = { ...seg };
          
          // Adjust start time of first segment
          if (idx === 0 && newSeg.startTime < part.startTime) {
            newSeg.startTime = part.startTime;
          }
          // Adjust end time of last segment
          if (idx === sorted.length - 1 && newSeg.endTime > part.endTime) {
            newSeg.endTime = part.endTime;
          }
          
          // Crop text for original language if part has explicit text selection
          const isOriginalLang = selectedLanguage === 'original' || 
                                 selectedLanguage === project.originalLanguage || 
                                 selectedLanguage === project.transcriptionLanguage || 
                                 selectedLanguage === 'en'; // fallback
          if (part.text && isOriginalLang) {
            const segText = seg.text || '';
            
            if (sorted.length === 1) {
              // Only one segment, the text is exactly part.text
              newSeg.text = part.text;
            } else if (idx === 0) {
              // First segment: should match the start of part.text
              // We find the longest prefix of segText that matches the start of part.text
              let matched = "";
              for (let len = segText.length; len >= 1; len--) {
                const sub = segText.substring(segText.length - len);
                if (part.text.startsWith(sub)) {
                  matched = sub;
                  break;
                }
              }
              if (matched) {
                newSeg.text = matched;
              }
            } else if (idx === sorted.length - 1) {
              // Last segment: should match the end of part.text
              let matched = "";
              for (let len = segText.length; len >= 1; len--) {
                const sub = segText.substring(0, len);
                if (part.text.endsWith(sub)) {
                  matched = sub;
                  break;
                }
              }
              if (matched) {
                newSeg.text = matched;
              }
            }
          }
          
          return newSeg;
        });
        
        // Prepare cropped translations
        if (project.translations) {
          Object.keys(project.translations).forEach(lang => {
            const langSegs = project.translations[lang] || [];
            croppedTranslations[lang] = langSegs.map(tSeg => {
              const matchingCrop = croppedTranscript.find(c => c.id === tSeg.id);
              if (matchingCrop) {
                const seg = sorted.find(s => s.id === tSeg.id);
                const segDuration = seg ? (seg.endTime - seg.startTime) : 0;
                
                let text = tSeg.text || '';
                if (seg && segDuration > 0 && text) {
                  const startRatio = (matchingCrop.startTime - seg.startTime) / segDuration;
                  const endRatio = (matchingCrop.endTime - seg.startTime) / segDuration;
                  
                  // Find nearest word boundaries to avoid cutting words in half
                  const startChar = Math.floor(text.length * startRatio);
                  const endChar = Math.floor(text.length * endRatio);
                  
                  // Try to align to word boundaries
                  let finalStart = startChar;
                  if (startChar > 0) {
                    const spaceBefore = text.lastIndexOf(' ', startChar);
                    const spaceAfter = text.indexOf(' ', startChar);
                    if (spaceBefore !== -1 && (startChar - spaceBefore < spaceAfter - startChar || spaceAfter === -1)) {
                      finalStart = spaceBefore + 1;
                    } else if (spaceAfter !== -1) {
                      finalStart = spaceAfter + 1;
                    }
                  }
                  
                  let finalEnd = endChar;
                  if (endChar < text.length) {
                    const spaceBefore = text.lastIndexOf(' ', endChar);
                    const spaceAfter = text.indexOf(' ', endChar);
                    if (spaceBefore !== -1 && (endChar - spaceBefore < spaceAfter - endChar || spaceAfter === -1)) {
                      finalEnd = spaceBefore;
                    } else if (spaceAfter !== -1) {
                      finalEnd = spaceAfter;
                    }
                  }
                  
                  text = text.substring(finalStart, finalEnd).trim();
                }
                
                return {
                  ...tSeg,
                  startTime: matchingCrop.startTime,
                  endTime: matchingCrop.endTime,
                  text: text
                };
              }
              return tSeg;
            });
          });
        }
      }
    }

    // Export options
    const exportOptions = {
      inputPath: overrideInputPath || project.localVideoPath || project.videoPath,
      startTime: part.startTime,
      endTime: part.endTime,
      transcript: croppedTranscript,
      translations: croppedTranslations,
      projectSettings: project.settings
    };
    
    // Add format if available
    const useGlobalManualFormat = project.settings?.formatSettingsEnabled;
    
    let activeFormat, activeAspectRatio, activeCustomization, activeMCX, activeMCY;
    
    if (useGlobalManualFormat) {
      activeFormat = project.settings?.format;
      activeAspectRatio = project.settings?.aspectRatio;
      activeCustomization = project.settings?.customization;
      activeMCX = project.settings?.manualCropX;
      activeMCY = project.settings?.manualCropY;
    } else {
      activeFormat = part.format;
      activeAspectRatio = part.aspectRatio;
      activeCustomization = part.customization;
      activeMCX = part.manualCropX;
      activeMCY = part.manualCropY;
    }
    
    if (activeFormat && activeFormat !== 'original') {
      const formatToAspectRatio = {
        '9:16': '9/16',
        '16:9': '16/9',
        '1:1':  '1/1',
        '4:5':  '4/5',
      };
      let resolvedAspectRatio = activeAspectRatio;
      if (activeFormat === 'advanced') {
        const canvasFmt = activeCustomization?.canvasFormat;
        resolvedAspectRatio = formatToAspectRatio[canvasFmt] || activeAspectRatio || '9/16';
        if (canvasFmt === 'custom') {
          const customW = project.settings?.customWidth || 16;
          const customH = project.settings?.customHeight || 9;
          resolvedAspectRatio = `${customW}/${customH}`;
        }
      } else {
        resolvedAspectRatio = activeAspectRatio || formatToAspectRatio[activeFormat] || null;
      }
      
      // Resolve dynamic variables (like replacing {part_name} with part.name in text layers)
      let resolvedCustomization = activeCustomization;
      if (activeFormat === 'advanced') {
        if (activeCustomization && activeCustomization.layers) {
          const resolvedLayers = activeCustomization.layers.map(layer => {
            if (layer.type === 'text') {
              const resolvedContent = layer.content.replace(/{part_name}/g, part.name || 'Part');
              return {
                ...layer,
                content: resolvedContent
              };
            }
            return layer;
          });
          resolvedCustomization = {
            ...activeCustomization,
            layers: resolvedLayers
          };
        }
      } else {
        // If it's a standard format, clear layers from customization so the backend does a simple crop
        resolvedCustomization = {
          faceTrackingEnabled: activeCustomization?.faceTrackingEnabled || false,
          faceTrackingSubMode: activeCustomization?.faceTrackingSubMode || 'automatic',
          faceTrackingMode: activeCustomization?.faceTrackingMode || 'center',
          faceTrackingSensitivity: activeCustomization?.faceTrackingSensitivity || 50,
          facePadding: activeCustomization?.facePadding || 20
        };
      }
      
      exportOptions.format = {
        aspectRatio: resolvedAspectRatio,
        formatType: activeFormat,
        customization: resolvedCustomization,
        manualCropX: activeMCX !== undefined ? activeMCX : 50,
        manualCropY: activeMCY !== undefined ? activeMCY : 50
      };
    }
    
    // ── Resolve watermark settings ────────────────────────────────────────
    const resolveWatermarks = (item) => {
      const results = [];
      
      // 1. Check for plural watermarks array first
      if (item.watermarks && Array.isArray(item.watermarks)) {
        item.watermarks.forEach(wm => {
          if (typeof wm === 'object' && wm !== null) results.push(wm);
        });
      }
      
      // 2. Check for watermarkNames (preset IDs)
      const wmNames = item.watermarkNames || (typeof item.watermark === 'string' ? [item.watermark] : []);
      if (wmNames.length > 0) {
        try {
          const savedPresets = JSON.parse(localStorage.getItem('watermarkPresets') || '[]');
          wmNames.forEach(nameId => {
            if (!nameId || nameId === 'none' || nameId === 'None') return;
            const preset = savedPresets.find(p => p.id === nameId || p.name === nameId);
            if (preset?.settings) {
              results.push(preset.settings);
            }
          });
        } catch (e) {
          console.error('💧 ExportManager: Failed to read watermark presets:', e);
        }
      }
      
      // 3. Backward compatibility: single watermark object
      if (results.length === 0 && item.watermarkSettings && typeof item.watermarkSettings === 'object') {
        results.push(item.watermarkSettings);
      }
      if (results.length === 0 && item.watermark && typeof item.watermark === 'object') {
        results.push(item.watermark);
      }
      
      return results;
    };

    let resolvedWatermarks = resolveWatermarks(part);

    // Fall back to global project watermark if enabled and no part-level watermarks
    if (resolvedWatermarks.length === 0 && project.settings?.watermark?.enabled) {
      resolvedWatermarks = [project.settings.watermark];
    }

    if (resolvedWatermarks.length > 0) {
      exportOptions.watermarks = resolvedWatermarks;
      // Also keep singular for backward compat in agent if needed
      exportOptions.watermarkSettings = resolvedWatermarks[0];
    }

    // Debug log for watermark
    console.log(`💧 ExportManager: Watermark payload (${resolvedWatermarks.length}):`, resolvedWatermarks.length > 0 ? {
      count: resolvedWatermarks.length,
      primary: {
        type: resolvedWatermarks[0].type,
        text: resolvedWatermarks[0].text,
        positionMode: resolvedWatermarks[0].positionMode,
      }
    } : 'NONE');
    console.log('📐 ExportManager: Format payload:', exportOptions.format || 'NONE (original)');

    
    // Add subtitles
    if (hasSubtitles) {
      let subtitleSettings = part.subtitleSettings || (project.settings?.subtitle?.enabled ? { ...project.settings.subtitle } : null);
      if (!subtitleSettings) {
        subtitleSettings = { enabled: true, fontSize: 36, fontColor: '#ffff00', position: 'bottom-center' };
      }
      
      exportOptions.subtitle = {
        enabled: subtitleSettings.enabled,
        settings: subtitleSettings,
        language: selectedLanguage,
        startTime: part.startTime,
        endTime: part.endTime
      };
    }

    if (window.require) {
      const { ipcRenderer } = window.require('electron');
      
      // Check FFmpeg availability
      const ffmpegCheck = await ipcRenderer.invoke('check-ffmpeg');
      if (!ffmpegCheck.available) {
        alert(`❌ FFmpeg Required\n\n${ffmpegCheck.message}`);
        return;
      }
      
      // Ask for folder if not provided
      let folderPath = forcedFolderPath;
      if (!folderPath) {
        folderPath = await ipcRenderer.invoke('select-folder', {
          title: `Select folder to save "${part.name}"`,
          defaultPath: cleanFilename(part.name || 'part')
        });
      }
      
      if (!folderPath) return;
      const outputPath = `${folderPath}/${fileName}`;
      exportOptions.outputPath = outputPath;
      
      if (!forcedFolderPath && !statusCallback) {
        alert(`🎬 Exporting part "${part.name}"...\nFrom: ${formatTime(part.startTime)} to ${formatTime(part.endTime)}\nThis may take a moment...`);
      }
      
      if (statusCallback) {
        statusCallback({ type: 'loading', message: `Exporting "${part.name}"...`, progress: 50 });
      }
      
      // Export via Electron
      const result = await ipcRenderer.invoke('export-video-segment', exportOptions);
      
      if (result.success) {
        if (!forcedFolderPath && !statusCallback) alert(`✅ Part exported successfully!\nSaved to: ${outputPath}`);
        if (statusCallback) statusCallback({ type: 'success', message: `✅ Exported: ${part.name}`, path: outputPath, folder: folderPath });
        return { success: true, path: outputPath, folder: folderPath };
      } else {
        if (statusCallback) statusCallback({ type: 'error', message: result.error || 'Export failed' });
        throw new Error(result.error || 'Export failed');
      }
    } else {
      // Browser fallback - use Local Agent
      
      // Check FFmpeg availability in Agent
      try {
        const ffmpegResponse = await fetch('http://localhost:8001/check-ffmpeg');
        const ffmpegData = await ffmpegResponse.json();
        if (!ffmpegData.available) {
          alert(`❌ FFmpeg Required in Local Agent\n\n${ffmpegData.message || 'FFmpeg is not installed or not in system PATH. Please install FFmpeg and restart the agent.'}`);
          return;
        }
      } catch (e) {
        alert('❌ Local Agent not found. Please make sure the VidoAI Local Agent is running on port 8001.');
        return;
      }

      // Check if we have a valid local path for the video
      let inputPath = overrideInputPath || project.localVideoPath || project.videoPath;
      
      if (!inputPath) {
        alert('🎬 To export via Local Agent, we need the video file.\nPlease select the original video file on your computer.');
        const fileResponse = await fetch('http://localhost:8001/select-file?title=Select Source Video File');
        const fileData = await fileResponse.json();
        if (fileData && fileData.path) {
          inputPath = fileData.path;
          exportOptions.inputPath = inputPath;
        } else {
          alert('❌ Input video file is required for export.');
          return;
        }
      }

      let folderPath = forcedFolderPath;
      if (!folderPath) {
        folderPath = await selectFolderViaAgentOrPrompt(
          `Select folder to save "${partName}" export`,
          getBrowserDefaultExportPath(),
          statusCallback
        );
        if (!folderPath) {
          return;
        }
      }
      
      if (!folderPath) return;
      const outputPath = `${folderPath}/${fileName}`;
      exportOptions.outputPath = outputPath;
      
      if (!forcedFolderPath && !statusCallback) {
        alert(`🎬 Exporting via Local Agent: "${partName}"...\nThis uses the backend processing.`);
      }

      if (statusCallback) {
        statusCallback({ type: 'loading', message: `Processing "${partName}" via Agent...`, progress: 30 });
      }
      
      const response = await fetch('http://localhost:8001/export', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(exportOptions)
      });
      
      const result = await response.json();
      if (result.success) {
        if (!forcedFolderPath && !statusCallback) alert(`✅ Export initiated!\nCheck the Local Agent for progress.\nSaved to: ${outputPath}`);
        if (statusCallback) statusCallback({ type: 'success', message: `✅ Exported: ${partName}`, path: outputPath, folder: folderPath });
        return { success: true, path: outputPath, folder: folderPath };
      } else {
        if (statusCallback) statusCallback({ type: 'error', message: result.message || 'Export failed via agent' });
        throw new Error(result.message || 'Export failed via agent');
      }
    }
  } catch (error) {
    console.error('❌ ExportManager: Export error:', error);
    if (!forcedFolderPath) alert(`❌ Export failed: ${error.message}`);
    throw error;
  }
};

// Placeholder for other export functions - will be added gradually
export const handleExportAllParts = async (parts, project, transcript, onUpdate = null, statusCallback = null) => {
  console.log('🎬 ExportManager: Exporting all individual parts:', parts.length);
  
  if (parts.length === 0) {
    alert('No individual parts to export');
    return;
  }

  try {
    let folderPath = null;
    let successCount = 0;
    
    if (window.require) {
      const { ipcRenderer } = window.require('electron');
      folderPath = await ipcRenderer.invoke('select-folder', {
        title: 'Select folder to export all parts',
        defaultPath: 'Exported_Parts'
      });
    } else {
      folderPath = await selectFolderViaAgentOrPrompt(
        'Select folder to export all parts',
        getBrowserDefaultExportPath(),
        statusCallback
      );
    }

    if (!folderPath) return;

    alert(`🎬 Starting export of ${parts.length} parts to ${folderPath}...\nThis may take some time.`);

    // Prompt for input video once if in browser mode and path is relative
    let overrideInputPath = null;
    if (!window.require) {
      const isAbsolutePath = (path) => {
        if (!path) return false;
        return /^[a-zA-Z]:\\/.test(path) || path.startsWith('\\\\') || path.startsWith('/') || path.startsWith('./') || path.startsWith('../');
      };
      
      const videoPath = project.localVideoPath || project.videoPath;
      if (!isAbsolutePath(videoPath) || videoPath.startsWith('http')) {
        alert('🎬 To export via Local Agent, we need the local path of your video file.\nPlease select the original video file on your computer.');
        const fileResponse = await fetch('http://localhost:8001/select-file?title=Select Source Video File');
        const fileData = await fileResponse.json();
        if (fileData.path) {
          overrideInputPath = fileData.path;
          // Save the absolute path to the project if onUpdate is provided
          if (onUpdate && !project.localVideoPath) {
            console.log('💾 Saving localVideoPath to project:', overrideInputPath);
            onUpdate({ localVideoPath: overrideInputPath });
          }
        } else {
          alert('❌ Export canceled: No video file selected.');
          return;
        }
      }
    }

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if (statusCallback) {
        statusCallback({ 
          type: 'loading', 
          message: `Exporting part ${i + 1}/${parts.length}: ${part.name || 'Part'}`,
          progress: Math.round(((i) / parts.length) * 100)
        });
      }
      await handleExportPart(part, project, transcript, folderPath, overrideInputPath, onUpdate, statusCallback);
      successCount++;
    }

    if (statusCallback) {
      statusCallback({ 
        type: 'success', 
        message: `✅ Exported ${successCount}/${parts.length} parts!`,
        folder: folderPath
      });
    } else {
      alert(`✅ Export completed: ${successCount}/${parts.length} parts exported successfully!`);
    }
  } catch (error) {
    console.error('❌ ExportManager: Export all parts error:', error);
    alert(`❌ Export failed: ${error.message}`);
  }
};

export const handleExportGroup = async (group, project, transcript, onUpdate = null, statusCallback = null) => {
  console.log('🎬 ExportManager: Exporting group:', group.name);
  
  const totalItems = (group.segments?.length || 0) + (group.parts?.length || 0);
  if (totalItems === 0) {
    if (statusCallback) statusCallback({ type: 'error', message: 'Group is empty' });
    else alert('Group is empty');
    return;
  }

  try {
    let folderPath = null;
    
    if (window.require) {
      const { ipcRenderer } = window.require('electron');
      folderPath = await ipcRenderer.invoke('select-folder', {
        title: `Select folder for group "${group.name}"`,
        defaultPath: cleanFilename(group.name)
      });
    } else {
      folderPath = await selectFolderViaAgentOrPrompt(
        `Select folder for group "${group.name}"`,
        getBrowserDefaultExportPath(),
        statusCallback
      );
    }

    if (!folderPath) return;

    // Prompt for input video once if in browser mode and path is relative
    let overrideInputPath = null;
    if (!window.require) {
      const isAbsolutePath = (path) => {
        if (!path) return false;
        return /^[a-zA-Z]:\\/.test(path) || path.startsWith('\\\\') || path.startsWith('/') || path.startsWith('./') || path.startsWith('../');
      };
      
      const videoPath = project.localVideoPath || project.videoPath;
      if (!isAbsolutePath(videoPath) || videoPath.startsWith('http')) {
        if (statusCallback) {
          statusCallback({ type: 'loading', message: 'Please select the original video file on your computer...', progress: 10 });
        } else {
          alert('🎬 To export via Local Agent, we need the local path of your video file.\nPlease select the original video file on your computer.');
        }
        
        overrideInputPath = await selectFileViaAgentOrPrompt(
          'Select Source Video File',
          typeof videoPath === 'string' && !videoPath.startsWith('http') ? videoPath : 'C:\\Users\\Inuobal\\Downloads\\video.mp4',
          statusCallback
        );
        
        if (overrideInputPath) {
          // Save the absolute path to the project if onUpdate is provided
          if (onUpdate && !project.localVideoPath) {
            console.log('💾 Saving localVideoPath to project:', overrideInputPath);
            onUpdate({ localVideoPath: overrideInputPath });
          }
        } else {
          if (statusCallback) statusCallback({ type: 'error', message: 'Export canceled: No video file selected.' });
          else alert('❌ Export canceled: No video file selected.');
          return;
        }
      }
    }

    if (!statusCallback) {
      alert(`🎬 Exporting group "${group.name}" (${totalItems} items) to ${folderPath}...`);
    } else {
      statusCallback({ 
        type: 'loading', 
        message: `Exporting group "${group.name}" (${totalItems} items)...`,
        progress: 20
      });
    }

    let successCount = 0;
    
    // Determine formats to process
    const formatsToProcess = group.formats && group.formats.length > 0 
      ? group.formats 
      : [group.format || 'original'];
      
    const formatMap = {
      '16:9': { name: 'YouTube', ratio: '16/9' },
      '9:16': { name: 'Shorts', ratio: '9/16' },
      '1:1': { name: 'Instagram', ratio: '1/1' },
      '4:5': { name: 'Instagram_Portrait', ratio: '4/5' }
    };
    
    for (let i = 0; i < formatsToProcess.length; i++) {
      const formatId = formatsToProcess[i];
      const isMultiFormat = formatsToProcess.length > 1;
      
      // Determine folder name for this format
      let formatFolderName = '';
      if (isMultiFormat) {
        if (group.formatNames && group.formatNames[i]) {
          formatFolderName = group.formatNames[i];
        } else {
          formatFolderName = formatMap[formatId]?.name || formatId;
        }
        formatFolderName = cleanFilename(formatFolderName);
      }
      
      const currentFolderPath = isMultiFormat ? `${folderPath}/${formatFolderName}` : folderPath;
      
      // Determine format settings
      const currentFormatType = formatId !== 'original' ? formatId : null;
      const currentAspectRatio = formatMap[formatId]?.ratio || (formatId !== 'original' ? formatId : null);
      
      const formatName = group.formatNames && group.formatNames[i];
      const resolvedPreset = resolvePresetDetails(formatId, formatName);
      
      // Process segments
      if (group.segments) {
        for (const segmentId of group.segments) {
          const segment = transcript.find(t => t.id === segmentId);
          if (segment) {
            const partFromSegment = {
              id: segment.id,
              name: `Segment_${segment.id}`,
              startTime: segment.startTime,
              endTime: segment.endTime,
              text: segment.text,
              language: group.language || 'original',
              format: currentFormatType || group.format,
              aspectRatio: currentAspectRatio || group.aspectRatio,
              customization: resolvedPreset ? resolvedPreset.customization : (group.customization || null),
              manualCropX: resolvedPreset ? resolvedPreset.manualCropX : (group.manualCropX !== undefined ? group.manualCropX : 50),
              manualCropY: resolvedPreset ? resolvedPreset.manualCropY : (group.manualCropY !== undefined ? group.manualCropY : 50),
              watermark: group.watermark,
              watermarkSettings: group.watermarkSettings,
              watermarks: group.watermarks,
              watermarkNames: group.watermarkNames,
              subtitleSettings: group.subtitleSettings,
              subtitlePresets: group.subtitlePresets,
              subtitlePresetNames: group.subtitlePresetNames
            };
            await handleExportPart(partFromSegment, project, transcript, currentFolderPath, overrideInputPath, onUpdate);
            successCount++;
          }
        }
      }
      
      // Process parts
      if (group.parts) {
        for (const part of group.parts) {
          const mergedPart = {
            ...part,
            language: group.language || part.language,
            format: currentFormatType || group.format || part.format,
            aspectRatio: currentAspectRatio || group.aspectRatio || part.aspectRatio,
            customization: resolvedPreset ? resolvedPreset.customization : (group.customization || part.customization),
            manualCropX: resolvedPreset ? resolvedPreset.manualCropX : (group.manualCropX !== undefined ? group.manualCropX : part.manualCropX),
            manualCropY: resolvedPreset ? resolvedPreset.manualCropY : (group.manualCropY !== undefined ? group.manualCropY : part.manualCropY),
            watermark: group.watermark || part.watermark,
            watermarkSettings: group.watermarkSettings || part.watermarkSettings,
            watermarks: group.watermarks || part.watermarks,
            watermarkNames: group.watermarkNames || part.watermarkNames,
            subtitleSettings: group.subtitleSettings || part.subtitleSettings,
            subtitlePresets: group.subtitlePresets || part.subtitlePresets,
            subtitlePresetNames: group.subtitlePresetNames || part.subtitlePresetNames
          };
          await handleExportPart(mergedPart, project, transcript, currentFolderPath, overrideInputPath, onUpdate);
          successCount++;
        }
      }
    }

    // ... inside group export loop ...
    if (statusCallback) {
      statusCallback({ 
        type: 'success', 
        message: `✅ Group "${group.name}" export completed!`,
        folder: folderPath
      });
    } else {
      alert(`✅ Group "${group.name}" export completed: ${successCount}/${totalItems} items exported!`);
    }
  } catch (error) {
    console.error('❌ ExportManager: Export group error:', error);
    alert(`❌ Export failed: ${error.message}`);
  }
};

export const handleExportSegment = async (segment, project, transcript) => {
  console.log('🎬 ExportManager: Export segment - TODO');
  // Implementation will be added later
}; 